import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { parceirosService } from '@/services/parceirosService';
import { isValidCPF, isValidCNPJ, formatCNPJ } from '@/utils/cpfValidation';
import { Loader2, MapPin, Mail, Phone, User, FileText, DollarSign, Users } from 'lucide-react';
import { ParceiroDetailedMetrics } from '@/types/parceiros';
import { useEstados, useCidades } from '@/hooks/useParceiros';
import { useVendedoresSimples } from '@/hooks/useVendedoresSimples';
import { usePermissions } from '@/hooks/usePermissions';

// Funções de formatação
const formatTelefone = (value: string) => {
  value = value.replace(/\D/g, '').slice(0, 11);
  if (value.length === 11) {
    return value.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  } else if (value.length === 10) {
    return value.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }
  return value;
};

const formatCPF = (value: string) => {
  value = value.replace(/\D/g, '').slice(0, 11);
  if (value.length === 11) {
    return value.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }
  return value;
};

interface EditarParceiroProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  parceiro: ParceiroDetailedMetrics | null;
}

type TipoPessoa = 'PF' | 'PJ';

export function EditarParceiro({ open, onClose, onSuccess, parceiro }: EditarParceiroProps) {
  const [tipoPessoa, setTipoPessoa] = useState<TipoPessoa>('PF');
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    cpf: '',
    pix: '',
    cidade: '',
    estado: '',
    vendedor_id: '',
    cnpj: '',
    razao_social: '',
    nome_fantasia: '',
    cpf_representante: ''
  });
  const [loading, setLoading] = useState(false);
  const permissions = usePermissions();
  const isAdmin = permissions.isAdmin();
  
  const { estados, loading: estadosLoading } = useEstados();
  const { cidades, loading: cidadesLoading } = useCidades(formData.estado);
  const { vendedores, loading: vendedoresLoading } = useVendedoresSimples();
  const [loadingCidadeValue, setLoadingCidadeValue] = useState(false);

  // Preencher formulário com dados do parceiro quando abrir
  useEffect(() => {
    if (parceiro && open) {
      setLoadingCidadeValue(true);
      const p = parceiro.parceiro;
      setTipoPessoa((p?.tipo_pessoa === 'PJ' ? 'PJ' : 'PF') as TipoPessoa);
      setFormData({
        nome: p?.nome || '',
        telefone: p?.telefone || '',
        cpf: p?.cpf || '',
        pix: p?.pix || '',
        cidade: '',
        estado: p?.estado || '',
        vendedor_id: p?.vendedor_id || '',
        cnpj: p?.cnpj || '',
        razao_social: p?.razao_social || '',
        nome_fantasia: p?.nome_fantasia || '',
        cpf_representante: p?.cpf_representante || ''
      });
    }
  }, [parceiro, open]);

  // Setar cidade após as cidades serem carregadas
  useEffect(() => {
    if (loadingCidadeValue && !cidadesLoading && cidades.length > 0 && parceiro?.parceiro?.cidade) {
      setFormData(prev => ({ ...prev, cidade: parceiro.parceiro?.cidade || '' }));
      setLoadingCidadeValue(false);
    }
  }, [loadingCidadeValue, cidadesLoading, cidades, parceiro]);

  // Limpar cidade quando estado mudar manualmente (apenas se for diferente do original)
  useEffect(() => {
    if (!loadingCidadeValue && formData.estado && parceiro && formData.estado !== parceiro.parceiro?.estado) {
      setFormData(prev => ({ ...prev, cidade: '' }));
    }
  }, [formData.estado, parceiro, loadingCidadeValue]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validação básica
    if (!formData.nome || !formData.telefone || !formData.cidade || !formData.estado) {
      toast.error('Nome, telefone, cidade e estado são obrigatórios!');
      return;
    }

    if (!parceiro?.parceiro?.id) {
      toast.error('ID do parceiro não encontrado');
      return;
    }

    if (tipoPessoa === 'PF') {
      if (formData.cpf) {
        const cpfDigits = formData.cpf.replace(/\D/g, '');
        if (cpfDigits.length !== 11) {
          toast.error('CPF inválido.');
          return;
        }
        if (!isValidCPF(formData.cpf)) {
          toast.error('CPF inválido.');
          return;
        }
      }
    } else {
      const cnpjDigits = formData.cnpj.replace(/\D/g, '');
      if (!cnpjDigits || cnpjDigits.length !== 14) {
        toast.error('CNPJ inválido.');
        return;
      }
      if (!isValidCNPJ(formData.cnpj)) {
        toast.error('CNPJ inválido.');
        return;
      }
      if (!formData.razao_social?.trim()) {
        toast.error('Razão social é obrigatória para Pessoa Jurídica.');
        return;
      }
      const cpfRepDigits = formData.cpf_representante.replace(/\D/g, '');
      if (!cpfRepDigits || cpfRepDigits.length !== 11) {
        toast.error('CPF do representante legal é obrigatório e inválido.');
        return;
      }
      if (!isValidCPF(formData.cpf_representante)) {
        toast.error('CPF do representante legal inválido.');
        return;
      }
    }

    setLoading(true);

    try {
      const updateData: any = {
        nome: tipoPessoa === 'PJ' ? formData.razao_social?.trim() : formData.nome,
        telefone: formData.telefone,
        pix: formData.pix || undefined,
        cidade: formData.cidade,
        estado: formData.estado,
        tipo_pessoa: tipoPessoa
      };
      if (tipoPessoa === 'PF') {
        updateData.cpf = formData.cpf || undefined;
      } else {
        updateData.cnpj = formData.cnpj.replace(/\D/g, '');
        updateData.razao_social = formData.razao_social.trim();
        updateData.nome_fantasia = formData.nome_fantasia?.trim() || undefined;
        updateData.cpf_representante = formData.cpf_representante.replace(/\D/g, '');
      }

      // Admin pode alterar o vendedor vinculado
      if (isAdmin && formData.vendedor_id) {
        updateData.vendedor_id = formData.vendedor_id;
      }

      await parceirosService.updateParceiro(parceiro.parceiro.id, updateData);

      toast.success('Parceiro atualizado com sucesso!');
      
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar parceiro');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Parceiro</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tipo: PF ou PJ */}
          <div className="space-y-2">
            <Label>Tipo de cadastro</Label>
            <Select value={tipoPessoa} onValueChange={(v: TipoPessoa) => setTipoPessoa(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PF">Pessoa Física (CPF)</SelectItem>
                <SelectItem value="PJ">Pessoa Jurídica (CNPJ)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Dados PJ: primeiros campos do formulário (Razão Social, CNPJ, Representante Legal, CPF) */}
          {tipoPessoa === 'PJ' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="razao_social" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Razão Social *
                </Label>
                <Input
                  id="razao_social"
                  value={formData.razao_social}
                  onChange={(e) => setFormData({ ...formData, razao_social: e.target.value })}
                  placeholder="Ex: Solar Tech Ltda"
                  required
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cnpj">CNPJ *</Label>
                <Input
                  id="cnpj"
                  value={formatCNPJ(formData.cnpj)}
                  onChange={(e) => setFormData({ ...formData, cnpj: e.target.value.replace(/\D/g, '') })}
                  placeholder="00.000.000/0000-00"
                  required
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nome_fantasia">Representante Legal *</Label>
                <Input
                  id="nome_fantasia"
                  value={formData.nome_fantasia}
                  onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })}
                  placeholder="Nome do representante legal"
                  required
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cpf_representante">CPF do Representante Legal *</Label>
                <Input
                  id="cpf_representante"
                  value={formatCPF(formData.cpf_representante)}
                  onChange={(e) => setFormData({ ...formData, cpf_representante: e.target.value.replace(/\D/g, '') })}
                  placeholder="000.000.000-00"
                  required
                  disabled={loading}
                />
              </div>
            </>
          )}

          {/* Nome Completo (apenas PF) */}
          {tipoPessoa === 'PF' && (
            <div className="space-y-2">
              <Label htmlFor="nome" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Nome Completo *
              </Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: João Silva"
                required
                disabled={loading}
              />
            </div>
          )}

          {/* Email (somente leitura) */}
          <div className="space-y-2">
            <Label htmlFor="email-display" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email
            </Label>
            <Input
              id="email-display"
              type="email"
              value={parceiro?.parceiro?.email || ''}
              placeholder="Email não disponível"
              disabled
              className="bg-muted cursor-not-allowed"
            />
            <p className="text-xs text-muted-foreground">
              O email não pode ser alterado
            </p>
          </div>

          {/* Telefone */}
          <div className="space-y-2">
            <Label htmlFor="telefone" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Telefone *
            </Label>
            <Input
              id="telefone"
              value={formatTelefone(formData.telefone)}
              onChange={(e) => setFormData({ ...formData, telefone: e.target.value.replace(/\D/g, '') })}
              placeholder="(00) 00000-0000"
              required
              disabled={loading}
            />
          </div>

          {/* Dados PF: CPF */}
          {tipoPessoa === 'PF' && (
            <div className="space-y-2">
              <Label htmlFor="cpf" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                CPF
              </Label>
              <Input
                id="cpf"
                value={formatCPF(formData.cpf)}
                onChange={(e) => setFormData({ ...formData, cpf: e.target.value.replace(/\D/g, '') })}
                placeholder="000.000.000-00"
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground">
                Se preenchido, deve ser válido e único no sistema.
              </p>
            </div>
          )}

          {/* PIX */}
          <div className="space-y-2">
            <Label htmlFor="pix" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Chave PIX
            </Label>
            <Input
              id="pix"
              value={formData.pix}
              onChange={(e) => setFormData({ ...formData, pix: e.target.value })}
              placeholder="Ex: email@exemplo.com, CPF, telefone ou chave aleatória"
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              Campo opcional - Chave PIX para recebimento de comissões
            </p>
          </div>

          {/* Estado */}
          <div className="space-y-2">
            <Label htmlFor="estado" className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Estado (UF) *
            </Label>
            <Select
              value={formData.estado}
              onValueChange={(value) => setFormData({ ...formData, estado: value })}
              disabled={loading || estadosLoading}
              required
            >
              <SelectTrigger id="estado">
                <SelectValue placeholder={estadosLoading ? "Carregando estados..." : "Selecione o estado"} />
              </SelectTrigger>
              <SelectContent>
                {estados.map((estado) => (
                  <SelectItem key={estado.sigla} value={estado.sigla}>
                    {estado.nome} - {estado.sigla}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Cidade */}
          <div className="space-y-2">
            <Label htmlFor="cidade" className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Cidade *
            </Label>
            <Select
              value={formData.cidade}
              onValueChange={(value) => setFormData({ ...formData, cidade: value })}
              disabled={!formData.estado || loading || cidadesLoading}
              required
            >
              <SelectTrigger id="cidade">
                <SelectValue 
                  placeholder={
                    !formData.estado 
                      ? "Selecione o estado primeiro" 
                      : cidadesLoading 
                        ? "Carregando cidades..." 
                        : "Selecione a cidade"
                  } 
                />
              </SelectTrigger>
              <SelectContent>
                {cidades.map((cidade) => (
                  <SelectItem key={cidade.id} value={cidade.municipio}>
                    {cidade.municipio}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!formData.estado && (
              <p className="text-xs text-muted-foreground">
                Selecione o estado primeiro
              </p>
            )}
          </div>

          {/* Vendedor Vinculado (Apenas para Admin) */}
          {isAdmin && (
            <div className="space-y-2">
              <Label htmlFor="vendedor" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Vendedor Responsável *
              </Label>
              <Select
                value={formData.vendedor_id}
                onValueChange={(value) => setFormData({ ...formData, vendedor_id: value })}
                disabled={loading || vendedoresLoading}
                required
              >
                <SelectTrigger id="vendedor">
                  <SelectValue 
                    placeholder={vendedoresLoading ? "Carregando vendedores..." : "Selecione o vendedor responsável"} 
                  />
                </SelectTrigger>
                <SelectContent>
                  {vendedores.map((vendedor) => (
                    <SelectItem key={vendedor.id} value={vendedor.id}>
                      {vendedor.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Alterar o vendedor responsável por este parceiro
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading || estadosLoading || cidadesLoading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar Alterações'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

